import { apiClient } from "../../../api";
import {
  ApiUser,
  CreateUserRequest,
  DeleteUserResponse,
  UpdateUserRequest,
  User,
  UserResponse,
  UsersListResponse,
} from "../types";

const transformPermissionsToOpsAssociations = (
  permissions: string[]
): string => {
  return permissions.filter(Boolean).join(",");
};

const transformApiUserToUser = (apiUser: ApiUser): User => {
  return {
    ...apiUser,
    permissions: apiUser.opsAssociations
      ? apiUser.opsAssociations.split(",").filter(Boolean)
      : [],
  };
};

export const getUsers = async (opsFilter?: string): Promise<User[]> => {
  const response = await apiClient.get<UsersListResponse>(
    `/gestorUserManagement/users${opsFilter !== "all" ? `?ops=${opsFilter}` : ""}`
  );

  if (!response.data.users) {
    return [];
  }

  const data = response.data.users.map(transformApiUserToUser);
  return data;
};

export const getUserById = async (id: string): Promise<User | null> => {
  const response = await apiClient.get<UserResponse>(
    `/gestorUserManagement/users/${id}`
  );

  if (!response.data.user) {
    return null;
  }

  return transformApiUserToUser(response.data.user);
};

export const createUser = async (
  email: string,
  password: string,
  name: string,
  permissions: string[]
): Promise<User> => {
  const requestData: CreateUserRequest = {
    email,
    password,
    name: name || undefined,
    opsAssociations: transformPermissionsToOpsAssociations(permissions),
  };

  try {
    const response = await apiClient.post<UserResponse>(
      "/gestorUserManagement/users",
      requestData
    );

    if (!response.data.user) {
      throw new Error(response.data.message || "Failed to create user");
    }

    return transformApiUserToUser(response.data.user);
  } catch (error: any) {
    if (error.response?.status === 409) {
      throw new Error("A user with this email already exists");
    }

    if (error.response?.status === 400) {
      throw new Error(
        error.response?.data?.message || "Invalid user data provided"
      );
    }

    throw new Error(
      error.response?.data?.message || error.message || "Failed to create user"
    );
  }
};

export const updateUser = async (
  id: string,
  updates: {
    email?: string;
    name?: string;
    permissions?: string[];
  }
): Promise<User> => {
  const requestData: UpdateUserRequest = {};

  if (updates.email !== undefined) {
    requestData.email = updates.email;
  }

  if (updates.name !== undefined) {
    requestData.name = updates.name;
  }

  if (updates.permissions !== undefined) {
    requestData.opsAssociations = transformPermissionsToOpsAssociations(
      updates.permissions
    );
  }

  try {
    const response = await apiClient.patch<UserResponse>(
      `/gestorUserManagement/users/${id}`,
      requestData
    );

    if (!response.data.user) {
      throw new Error(response.data.message || "Failed to update user");
    }

    return transformApiUserToUser(response.data.user);
  } catch (error: any) {
    if (error.response?.status === 404) {
      throw new Error("User not found");
    }

    if (error.response?.status === 409) {
      throw new Error("Email already in use by another user");
    }

    if (error.response?.status === 400) {
      throw new Error(
        error.response?.data?.message || "Invalid user data provided"
      );
    }

    throw new Error(
      error.response?.data?.message || error.message || "Failed to update user"
    );
  }
};

export const deleteUser = async (id: string): Promise<void> => {
  try {
    // apiClient.transformResponse always sets success: true on the ApiResponse
    // wrapper, so we only need to call the endpoint and let HTTP errors propagate
    // to the catch block via axios interceptors.
    await apiClient.delete<DeleteUserResponse>(
      `/gestorUserManagement/users/${id}`
    );
  } catch (error: any) {
    if (error.response?.status === 404) {
      throw new Error("User not found");
    }

    throw new Error(
      error.response?.data?.message || error.message || "Failed to delete user"
    );
  }
};
